import { purchaseOrders } from "@/api/purchaseOrder/usePurchaseOrder";
import { mutatePurchaseOrderItems } from "@/api/purchaseOrderItems/usePurchaseOrderItems";
import { users } from "@/api/users/useUsersQuery";
import { ItemAutoComplete } from "@/custom/features/ui/autoComplete/item/item-autoComplete";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/form/FormButtonBox";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormSelect } from "@/features/standard/form/FormSelect";
import { FormWrapper } from "@/features/standard/form/FormWrapper";
import customAlert from "@/features/ui/alert/Alert";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { PartnerAutoComplete } from "@/features/ui/autoComplete/partner/partner-autoComplete";
import { PurchaseOrderAutoComplete } from "@/features/ui/autoComplete/purchaseOrder/purchaseOrders-autoComplete";
import { UserAutoComplete } from "@/features/ui/autoComplete/user/user-autoComplete";
import { handleErrorResponse } from "@/utils/errorMessage";
import { translateWithPartnerType } from "@/utils/partnerType";
import { validateFormValues } from "@/utils/validation";
import { Button, Flex, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  PartnersGet200ResponseRowsInnerTypeEnum,
  PurchaseOrdersGet200ResponseRowsInner,
  ScmApiPurchaseOrderItemsPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const Create = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Select: FormSelect,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  Text: Text,
  Flex: Flex,
  DateInput: DateInput,
});

interface OrderItemCreateFormProps {
  orderId?: number;
  order?: PurchaseOrdersGet200ResponseRowsInner;
}

export const OrderItemsCreateForm = (props: OrderItemCreateFormProps) => {
  const { orderId, order } = props;
  const { closeModal } = useModal();
  const { data: userInfo } = useQuery(users.who({}));
  const partnerType = userInfo?.data?.partner?.type;

  const { t } = useTranslation();
  const [id, setId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { mutate: createMutate } = useMutation(
    (params: ScmApiPurchaseOrderItemsPostRequest) =>
      mutatePurchaseOrderItems
        .create(params)
        .mutationFn(params as ScmApiPurchaseOrderItemsPostRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["purchaseOrderItems"]);
        closeModal(form.values);
        customAlert(
          t("message.createSuccess", {
            item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
          }),
          t("common.createSuccess"),
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({
            error,
            errorContext: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
          }),
          t("common.createFail", {
            item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
          }),
          "red",
        );
      },
    },
  );

  const { data: orderItems } = useQuery({
    ...purchaseOrders.detail({
      purchaseOrderId: orderId ?? Number(id) ?? 0,
      populate: ["location", "user", "partner", "purchaseOrderItems"],
    }),
    enabled: !!orderId || !!id,
  });

  const getInitialValues = (
    order: PurchaseOrdersGet200ResponseRowsInner | undefined,
    orderItems: AxiosResponse<PurchaseOrdersGet200ResponseRowsInner, any> | undefined,
  ) => {
    return {
      purchaseOrderId: orderId
        ? orderId
        : order
          ? order?.id
          : orderItems
            ? orderItems?.data?.id
            : undefined,
      partnerCode: orderItems?.data?.partnerCode ?? order?.partnerCode ?? undefined,
      itemCode: undefined,
      quantity: undefined,
      orderDate: order?.orderDate
        ? new Date(order?.orderDate)
        : orderItems
          ? new Date(orderItems.data?.orderDate ?? "")
          : undefined,
      deliveryDeadline: order?.deliveryDeadline
        ? new Date(order?.deliveryDeadline)
        : orderItems
          ? new Date(orderItems?.data?.deliveryDeadline ?? "")
          : undefined,
      userCode: orderItems?.data?.userCode ?? order?.userCode ?? undefined,
      targetLocationCode:
        orderItems?.data?.targetLocationCode ?? order?.targetLocationCode ?? undefined,
    };
  };

  const form = useForm({
    initialValues: getInitialValues(order, orderItems),
  });

  useEffect(() => {
    if (orderItems) {
      form.setValues(getInitialValues(order, orderItems));
    }
  }, [orderItems]);

  const onSubmit = async () => {
    try {
      createMutate({
        purchaseOrderItemsGetRequest: {
          purchaseOrderId: orderId
            ? orderId
            : order
              ? order?.id
              : orderItems
                ? orderItems?.data?.id
                : form.values.purchaseOrderId!,
          partnerCode:
            order?.partnerCode ?? orderItems?.data?.partnerCode ?? form.values.partnerCode!,
          direction: "OUTBOUND",
          itemCode: form.values.itemCode!,
          quantity: form.values.quantity!,
          orderDate:
            order?.orderDate?.toString()! ??
            orderItems?.data?.orderDate?.toString()! ??
            form.values.orderDate!,
          deliveryDeadline:
            order?.deliveryDeadline?.toString()! ??
            orderItems?.data?.deliveryDeadline?.toString()! ??
            form.values.deliveryDeadline!,
          userCode: order?.userCode ?? orderItems?.data?.userCode ?? form.values.userCode!,
          targetLocationCode:
            order?.targetLocationCode ??
            orderItems?.data?.targetLocationCode ??
            form.values.targetLocationCode!,
        },
      });
    } catch (e) {
      customAlert(
        t("message.createFail", {
          item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
        }),
        t("common.createFail", {
          item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
        }),
        "red",
      );
    }
  };

  return (
    <>
      <Create.Wrapper>
        {orderId || order ? null : (
          <PurchaseOrderAutoComplete
            direction="OUTBOUND"
            label={translateWithPartnerType(t, "order.code", partnerType)}
            placeholder={t("placeholder.select", {
              item: translateWithPartnerType(t, "order.code", partnerType),
            })}
            {...form.getInputProps("purchaseOrderId")}
            clearable
            onChange={(e) => setId(e)}
          />
        )}
        <PartnerAutoComplete
          label={t("partner.partner")}
          placeholder={t("placeholder.select", { item: t("partner.partner") })}
          {...form.getInputProps("partnerCode")}
          partnerType={[
            partnerType ?? PartnersGet200ResponseRowsInnerTypeEnum.PURCHASE,
            PartnersGet200ResponseRowsInnerTypeEnum.BI_DIRECTION,
          ]}
          withAsterisk
        />
        <ItemAutoComplete
          label={t("item.name")}
          getItemType={["PRODUCT", "SEMI_PRODUCT", "MATERIAL", "SUBSIDIARY"]}
          placeholder={t("placeholder.select", { item: t("item.name") })}
          {...form.getInputProps("itemCode")}
        />
        <Create.Input
          label={t("inventory.quantity")}
          placeholder={t("placeholder.input", { item: t("inventory.quantity") })}
          {...form.getInputProps("quantity")}
        />
        <UserAutoComplete
          label={t("order.manager")}
          placeholder={t("placeholder.select", { item: t("order.manager") })}
          {...form.getInputProps("userCode")}
        />
        <LocationAutoComplete
          label={translateWithPartnerType(t, "order.toLocationName", partnerType)}
          placeholder={t("placeholder.select", {
            item: translateWithPartnerType(t, "order.toLocationName", partnerType),
          })}
          {...form.getInputProps("targetLocationCode")}
        />
        <Create.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          label={translateWithPartnerType(t, "order.orderDate", partnerType)}
          valueFormat="YYYY-MM-DD"
          locale="ko"
          firstDayOfWeek={0}
          {...form.getInputProps("orderDate")}
        />
        <Create.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          label={t("order.deliveryDate")}
          valueFormat="YYYY-MM-DD"
          locale="ko"
          firstDayOfWeek={0}
          {...form.getInputProps("deliveryDeadline")}
        />
        <Create.ButtonBox>
          <Create.Button color="gray" onClick={closeModal}>
            {t("common.cancel")}
          </Create.Button>
          <Create.Button
            disabled={validateFormValues({
              pageType: "purchaseOrderItems",
              values: form.values,
            })}
            onClick={onSubmit}
          >
            {t("common.additional", {
              item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
            })}
          </Create.Button>
        </Create.ButtonBox>
      </Create.Wrapper>
    </>
  );
};
